import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { OtpRequest, ResendOtpRequest } from '../core/models';
import { AuthData } from '../core/data/AuthData';
import { LocalStorageService } from '../core/services/shared-services/local-storage.service';
import { UtilityService } from '../core/services/shared-services/utility.service';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription, interval } from 'rxjs';
import { GtmService, GtmEventName, GtmLoginMethod, GtmUserType } from '../core/services/shared-services/gtm.service';

@Component({
    selector: 'app-otp',
    templateUrl: './app.otp.component.html',
    styleUrls: ['./app.otp.component.scss'],
    standalone: false
})
export class AppOtpComponent {
  userId: string;
  disableOtpSubmitButton: boolean = false;
  userEmail: string = '';
  userName: string = '';
  timerSubscription: Subscription;
  timerInSeconds: number;
  timerActive: boolean = false;
  disableResendBtn: boolean = false;
  latestYear: number = new Date().getFullYear();
  public otpForm: FormGroup;

  constructor(
    private _route: Router,
    private authServiceApi: AuthData,
    private localStorageService: LocalStorageService,
    private utilityService: UtilityService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private gtm: GtmService,
  ) {
    const checkdata = this._route.getCurrentNavigation();
    if (this.localStorageService.getItem('user') != null) {
      if (this.authServiceApi.isLoggedIn()) {
        const userRole = this.authServiceApi.getRole();
        if (userRole?.toLowerCase() == 'admin') {
          this._route.navigate(['/admin/dashboard']);
        } else if (userRole?.toLowerCase() == 'enterprise sim') {
          this._route.navigate(['/enterprise-sim/dashboard']);
        } else {
          this._route.navigate(['/selfcare/dashboard']);
        }
      }
    }
    if (checkdata != null || checkdata != undefined) {
      this.userId = this._route.getCurrentNavigation().extras?.state?.UserId;
      if (this.userId == null || this.userId == undefined) {
        this._route.navigate(['/login']);
      }
    }

    this.startTimer();

    this.otpForm = new FormGroup({
      otp: new FormControl(null, [Validators.required,  Validators.pattern(/^\d{1,4}$/)]),
    });
  }

  submit() {
    if (this.otpForm.valid) {
      var otpRequest: OtpRequest = {
        userId: this.userId,
        otp: this.formValues().otp,
      };
      this.disableOtpSubmitButton = true;
      this.disableResendBtn = true;
      this.authServiceApi.validateOtp(otpRequest).subscribe({
        next: (v) => {
          if (v.data !== null && v.data?.isPasswordExpired && v.data?.resetLink !== '') {
            this.messageService.add({
              severity: 'info',
              summary: 'Success',
              detail: 'Your password has been expired, please reset your password.',
            });
            const queryParams = {
              token: v.data?.resetLink,
            };
            this._route.navigate(['reset'], { queryParams });
          } else {
            if (v.data?.isPasswordAboutToExpire) {
              this.messageService.add({
                severity: 'info',
                summary: 'Success',
                detail: 'Your password is about to expire, please reset your password.',
              });
            }
            this.userEmail = v?.data?.userName;
            let data = {
              uesrName: this.userName,
              userEmail: this.userEmail,
            };
            this.utilityService.userInformation(data);
            if (v != null && v.success == true && v.data != null) {
              this.gtm.push({
                event: GtmEventName.LoginSuccess,
                method: GtmLoginMethod.Otp,
                user_type: this.resolveUserType(v.data.role),
              });
              if (v.data?.isFirstLogin) {
                this.messageService.add({
                  severity: 'info',
                  summary: 'Success',
                  detail: 'Your password will expire, please change your password.',
                });
              }
              if (!this.authServiceApi.isLoggedIn()) {
                this.localStorageService.setItem('user', JSON.stringify(v.data));

                let registerDevice = {
                  device_id: localStorage.getItem('device_id'),
                  user_identity: v.data.id,
                };
                if (registerDevice.device_id) {
                  this.utilityService.registerdevice(registerDevice).subscribe({
                    next: (r) => {},
                  });
                }
                if (
                  v.data.role?.toLowerCase() == 'admin' ||
                  v.data.role?.toLowerCase() == 'tms' ||
                  v.data.role?.toLowerCase() == 'cms'
                ) {
                  this._route.navigate(['admin']);
                } else {
                  if (this.authServiceApi.isSelfCareUser()) {
                    this._route.navigate(['selfcare']);
                  } else if(v.data.role?.toLowerCase() == 'sales agent' ) {
                    this._route.navigate(['sales-agent']);
                  } else if (v.data.role?.toLowerCase() == 'enterprise sim') {
                    this._route.navigate(['enterprise-sim']);
                  } else {
                    localStorage.clear();
                    this.messageService.add({
                      severity: 'error',
                      summary: 'Error',
                      detail: 'Invalid OTP. Please enter it again',
                    });
                  }
                }
              }
              this.authServiceApi.isLoggedIn$.next(true);
              this.disableOtpSubmitButton = false;
              this.disableResendBtn = false;
            } else {
              this.disableOtpSubmitButton = false;
              this.disableResendBtn = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: v?.responseMessage_en ?? 'Invalid OTP. Please try again.',
              });
            }
          }
        },
        error: err => {
          this.disableOtpSubmitButton = true;
          this.disableResendBtn = false;
        }
      });
    }
  }
  private resolveUserType(role: string): typeof GtmUserType[keyof typeof GtmUserType] {
    const r = role?.toLowerCase() ?? '';
    if (r === 'admin' || r === 'tms' || r === 'cms' || r === 'selfcare admin') return GtmUserType.SmeAdmin;
    if (r === 'shared access') return GtmUserType.SharedAccess;
    if (r === 'sales agent') return GtmUserType.SalesAgent;
    return GtmUserType.SmeUser;
  }

  formValues() {
    return this.otpForm.value;
  }

  startTimer() {
    this.timerActive = true;
    this.timerInSeconds = 90;
    this.timerSubscription = interval(1000).subscribe(() => {
      if (this.timerInSeconds > 0) {
        this.timerInSeconds--;
      } else {
        this.timerActive = false;
        this.disableResendBtn = false;
        this.timerSubscription.unsubscribe();
      }
    });
  }
  resendOtp() {
    this.disableResendBtn = true;
    var resendOtpRequest: ResendOtpRequest = {
      UserId: this.userId,
      language: this.translocoService.getActiveLang(),
    };
    this.authServiceApi.resendOtp(resendOtpRequest).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'info',
          summary: 'Success',
          detail: res.responseMessage_en ?? 'OTP sent to email or sms',
        });
        this.startTimer();
      },
      error: (err) => {
        this.disableResendBtn = false;
      },
    });
  }
}
